import type { GetImpulseFormOutput, GetImpulseFormOutputVerbose } from "~/tools/impulse-form";

import type { StatChart } from "../../../../common/dashboard-spec/chart-schemas";
import { WidgetColor } from "../../../widget-form/color/form-state";
import { StatChartFormState } from "../../../widget-form/stat-chart-form-state";
import type { WidgetFormSync } from "../../../widget-form/sync";
import type { FormProcessor } from "../../_form-processor";
import { StepFormState as ThresholdStepFormState } from "../_thresholds/step-form-state";

type Spec = StatChart["spec"];

const DEFAULT = StatChartFormState.default;

function specToInput({ format, sparkline, metricLabel, valueFontSize, calculation, thresholds }: Spec) {
	return {
		unit: format?.unit ?? DEFAULT.unit,
		decimals: format?.decimalPlaces?.toString() ?? DEFAULT.decimals,
		sparkline: sparkline ?? DEFAULT.sparkline,
		metricLabel: metricLabel ?? DEFAULT.metricLabel,
		fontSize: valueFontSize?.toString() ?? DEFAULT.fontSize,
		calculation: calculation ?? DEFAULT.calculation,
		color: thresholds?.defaultColor ?? DEFAULT.color,
		thresholds: {
			steps: thresholds?.steps.map(ThresholdStepFormState.specToInput) ?? DEFAULT.thresholds,
		},
	} satisfies WidgetFormSync;
}

type FormState = StatChartFormState;

function outputToSpec({
	unit,
	decimals,
	sparkline,
	metricLabel,
	fontSize,
	calculation,
	color,
	thresholds,
}: GetImpulseFormOutput<FormState>): Spec {
	return {
		calculation,
		metricLabel,
		sparkline,
		valueFontSize: fontSize,

		format: {
			unit,
			decimalPlaces: decimals,
		},

		thresholds: {
			defaultColor: color,
			steps: thresholds.map(ThresholdStepFormState.outputToSpec),
		},
	};
}

function verboseOutputToSpec({
	unit,
	decimals,
	sparkline,
	metricLabel,
	fontSize,
	calculation,
	color,
	thresholds,
}: GetImpulseFormOutputVerbose<FormState>): Spec {
	return {
		calculation: calculation ?? undefined,
		metricLabel: metricLabel ?? undefined,
		sparkline: sparkline ?? undefined,
		valueFontSize: fontSize ?? undefined,

		format: {
			unit: unit ?? undefined,
			decimalPlaces: decimals ?? undefined,
		},

		thresholds: {
			defaultColor: color ?? WidgetColor.healthy,
			steps: thresholds.map(ThresholdStepFormState.verboseOutputToSpec),
		},
	};
}

type Input = ReturnType<typeof specToInput>;

function init(sync?: Input): FormState {
	const initial = sync == null ? undefined : StatChartFormState.fromSync(sync);

	return StatChartFormState.init(initial);
}

function setInput(input: Input, state: FormState): void {
	StatChartFormState.setInput(StatChartFormState.fromSync(input), state);
}

function resetToDefault(state: FormState): void {
	StatChartFormState.setInput(StatChartFormState.default, state);
}

const FormState = {
	specToInput,
	outputToSpec,
	verboseOutputToSpec,
	init,
	setInput,
	resetToDefault,
} satisfies FormProcessor<Spec, FormState, Input>;

export { FormState };
